import React, { useMemo } from 'react';
import { Button, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { IImportProblem } from '../../../../utils/asset-type-import/i-import-job';
import { MAX_PROBLEM_ROWS, PROBLEM_PAGE_SIZE } from '../../../../utils/asset-type-import/constants';
import {
  BODY_CELL, HEADER_CELL, MUTED_TEXT, ProblemTag,
} from './import-tags';

/**
 * The read-only problem report. [AVP-891]
 *
 * Read-only on purpose: the old wizard let you edit rows in place, which only
 * worked because the whole file was in the browser. The file now lives on the
 * server, so fixing a row means fixing the source and importing again.
 *
 * Only the first page or so is fetched. A mis-mapped 200 000-row file produces
 * 200 000 problems, and an antd Table is not the place to put them — the rollup
 * above the table is what actually answers "what went wrong", and the CSV has
 * every row for anyone who needs them.
 */

export interface IProblemTableProps {
  rows: IImportProblem[];
  total: number;
  /** the server stopped recording problems; the report is not complete */
  truncated: boolean;
  page: number;
  loading?: boolean;
  onPageChange: (page: number) => void;
  onDownload: () => void;
  downloading?: boolean;
}

const columns: ColumnsType<IImportProblem> = [
  {
    title: 'Line',
    dataIndex: 'lineNo',
    key: 'lineNo',
    width: 90,
    render: (line: number | null) => (line ?? '—'),
    onHeaderCell: () => ({ style: HEADER_CELL }),
    onCell: () => ({ style: BODY_CELL }),
  },
  {
    title: 'Code',
    dataIndex: 'code',
    key: 'code',
    width: 220,
    render: (code: string | null) => (code && code.trim() !== '' ? code : '—'),
    onHeaderCell: () => ({ style: HEADER_CELL }),
    onCell: () => ({ style: BODY_CELL }),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: 130,
    render: (status: string) => <ProblemTag status={status} />,
    onHeaderCell: () => ({ style: HEADER_CELL }),
    onCell: () => ({ style: BODY_CELL }),
  },
  {
    title: 'Reason',
    dataIndex: 'message',
    key: 'message',
    onHeaderCell: () => ({ style: HEADER_CELL }),
    onCell: () => ({ style: BODY_CELL }),
  },
];

/**
 * Groups the rows currently on screen by message, so a mis-mapped file reads
 * as a few kinds of problem instead of fifty identical lines.
 *
 * Only this page is counted — the rest of the report lives on the server — so
 * the caller must label it as such rather than let the numbers read as totals.
 */
export const rollupOf = (rows: IImportProblem[]): Array<{ message: string; count: number }> => {
  const counts = new Map<string, number>();
  rows.forEach((row) => {
    const key = row.message ?? 'Unknown problem';
    counts.set(key, (counts.get(key) ?? 0) + 1);
  });
  return [...counts.entries()]
    .map(([message, count]) => ({ message, count }))
    .sort((left, right) => right.count - left.count);
};

const ProblemTable = (props: IProblemTableProps): JSX.Element => {
  const {
    rows, total, truncated, page, loading, onPageChange, onDownload, downloading,
  } = props;

  const shown = Math.min(total, MAX_PROBLEM_ROWS);
  const rollup = useMemo(() => rollupOf(rows), [rows]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      }}
      >
        <span style={{ fontSize: 13 }}>
          {total > MAX_PROBLEM_ROWS
            ? `Showing ${shown.toLocaleString()} of ${total.toLocaleString()} rows with problems`
            : `${total.toLocaleString()} row${total === 1 ? '' : 's'} with problems`}
        </span>
        <Button size="small" onClick={onDownload} loading={downloading}>
          Download CSV report
        </Button>
      </div>

      {truncated ? (
        <div style={{ ...MUTED_TEXT, color: '#E13333' }}>
          The report itself was capped, so some problem rows were not recorded. The counts above
          the table are still exact.
        </div>
      ) : null}

      {rollup.length > 1 ? (
        <div style={MUTED_TEXT}>
          <div>On this page:</div>
          {rollup.slice(0, 4).map((entry) => (
            <div key={entry.message}>{`${entry.count.toLocaleString()} × ${entry.message}`}</div>
          ))}
          {total > rows.length ? <div>Download the CSV for the full breakdown.</div> : null}
        </div>
      ) : null}

      <div style={{ border: '1px solid #f1f2f5', borderRadius: 11, overflow: 'hidden' }}>
        <Table
          size="small"
          columns={columns}
          dataSource={rows}
          loading={loading}
          rowKey={(row, index) => `${row.lineNo ?? 'x'}-${row.code ?? ''}-${index}`}
          bordered={false}
          scroll={{ y: 320 }}
          pagination={{
            current: page,
            pageSize: PROBLEM_PAGE_SIZE,
            total: shown,
            showSizeChanger: false,
            size: 'small',
            hideOnSinglePage: true,
            onChange: onPageChange,
          }}
        />
      </div>
    </div>
  );
};

ProblemTable.displayName = 'ProblemTable';

export default ProblemTable;
